import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sun, Moon, RotateCcw, Activity } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { pages, pagesById, type PageId } from "../pages/registry";
import { BridgeApi, BusApi } from "../lib/api";
import { qk } from "../lib/query";
import { ensureStreamOpen, useStreamHealth } from "../lib/sse";
import { useTheme } from "../lib/theme";
import { useToast } from "../lib/toast";
import { cn } from "../lib/cn";

/**
 * Mobile chrome: a compact top header + a full-bleed content area + a
 * horizontally-scrollable bottom tab bar showing EVERY destination
 * (swipe left/right). Pages themselves are shared with desktop — only
 * the surrounding chrome differs.
 */
export function MobileShell({
  page,
  onSelect,
}: {
  page: PageId;
  onSelect: (id: PageId) => void;
}) {
  const Page = pagesById[page].Component;
  const pageDef = pagesById[page];

  // h-[100dvh], not h-screen (100vh): on mobile browsers 100vh includes the
  // area behind the address bar/toolbar, so the header + bottom nav get pushed
  // out of view and you have to scroll to reach them. dvh tracks the
  // actually-visible viewport and adjusts as the toolbar retracts.
  return (
    <div className="h-[100dvh] w-screen flex flex-col overflow-hidden text-sm bg-app">
      <MobileHeader title={pageDef?.label ?? "Agent Fleet"} />
      <div className="flex-1 min-h-0 overflow-auto">
        <Page />
      </div>
      <MobileTabBar active={page} onSelect={onSelect} />
    </div>
  );
}

// ── Top header ──────────────────────────────────────────────────────────

function MobileHeader({ title }: { title: string }) {
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const qc = useQueryClient();

  const { data: bridgeStatus } = useQuery({
    queryKey: qk.bridge.status,
    queryFn: BridgeApi.status,
    refetchInterval: 15000,
  });

  useEffect(() => { ensureStreamOpen(); }, []);
  const stream = useStreamHealth();

  const restart = useMutation({
    mutationFn: BridgeApi.restart,
    onSuccess: () => {
      toast.success("agent-bridge restart issued");
      qc.invalidateQueries({ queryKey: qk.bridge.status });
    },
    onError: (err) => toast.error("Restart failed", String(err)),
  });

  return (
    <header className="h-12 flex-shrink-0 border-b border-border bg-surface/80 backdrop-blur-sm flex items-center gap-2 px-3 pt-[env(safe-area-inset-top)]">
      <div className="size-6 rounded-md bg-accent flex items-center justify-center text-fg-inverse font-bold text-[10px] flex-shrink-0">
        AF
      </div>
      <span className="text-sm font-semibold text-fg truncate flex-1">{title}</span>

      {/* status dot */}
      <div className="flex items-center gap-1 h-7 px-1.5 rounded-md bg-surface-2 border border-border">
        <Activity className={cn("size-3", bridgeStatus?.running ? "text-success" : "text-danger")} />
        <span className={cn("size-1.5 rounded-full", stream.alive ? "bg-success animate-pulse-dot" : "bg-fg-subtle")} />
      </div>

      <button
        onClick={toggle}
        className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-surface-2 border border-border text-fg-muted ring-focus"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
      </button>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-surface-2 border border-border text-fg-muted ring-focus"
            aria-label="Operations"
          >
            <RotateCcw className="size-3.5" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="panel-elevated p-1 min-w-[200px] shadow-lg z-[300] animate-fade-in"
          >
            <DropdownMenu.Item
              onSelect={() => { if (confirm("Restart agent-bridge?")) restart.mutate(); }}
              className="flex items-center gap-2 px-2 py-2 text-sm text-fg rounded outline-none focus:bg-surface-2 cursor-pointer"
            >
              <RotateCcw className="size-3.5" />
              Restart agent-bridge
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  );
}

// ── Bottom tab bar (all destinations, horizontally scrollable) ────────────

function MobileTabBar({ active, onSelect }: { active: PageId; onSelect: (id: PageId) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const { data: busStats } = useQuery({
    queryKey: qk.bus.stats,
    queryFn: BusApi.stats,
    refetchInterval: 8000,
  });
  const badges: Partial<Record<PageId, number>> = { "bus-inbox": busStats?.unread ?? 0 };

  // Keep the active tab scrolled into view when the page changes.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [active]);

  return (
    <nav
      ref={scrollRef}
      className="flex-shrink-0 border-t border-border bg-surface flex overflow-x-auto no-scrollbar pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      {pages.map((p) => {
        const Icon = p.icon;
        const isActive = p.id === active;
        const badge = badges[p.id] ?? 0;
        return (
          <button
            key={p.id}
            ref={isActive ? activeRef : undefined}
            onClick={() => onSelect(p.id)}
            className={cn(
              "relative flex flex-col items-center justify-center gap-0.5 flex-shrink-0 w-[4.5rem] py-2 ring-focus",
              isActive ? "text-accent" : "text-fg-muted",
            )}
          >
            {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-accent rounded-b-full" />}
            <div className="relative">
              <Icon className="size-5" />
              {badge > 0 && (
                <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center h-3.5 min-w-[0.875rem] px-1 rounded-full bg-accent text-fg-inverse text-[9px] font-semibold">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </div>
            <span className="text-[10px] leading-none font-medium truncate max-w-full">{p.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
